import type { ComponentTemplate } from "@v1/types/component-template";

import * as React from "react";

import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import ComponentTemplateDragOverlay from "@v1/components/ComponentTemplateDragOverlay";
import IsolatedReploComponent from "@v1/components/IsolatedReploComponent";
import { getOptimizedImageUrl } from "@v1/utils/component-template";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import { ImageOff } from "lucide-react";

export const SectionTemplateListSkeleton = ({
  hideTitle,
  count = 3,
}: {
  hideTitle?: boolean;
  count?: number;
}) => {
  return (
    <div className={twMerge("flex flex-col gap-3", hideTitle ? "" : "p-3")}>
      {!hideTitle && (
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-32" />
          </div>
        </div>
      )}
      <ul className="mb-8 mt-1 flex flex-col gap-3">
        {new Array(count).fill(0).map((_, index) => (
          <li key={index}>
            <div className="aspect-[16/9] w-full">
              <Skeleton className="border-border h-full w-full rounded-lg border-[0.5px]" />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ComponentPreview = ({
  componentTemplate,
  imageError,
  setImageError,
}: {
  componentTemplate: ComponentTemplate;
  imageError: boolean;
  setImageError: (error: boolean) => void;
}) => {
  const [imageLoading, setImageLoading] = React.useState(true);

  if (isFeatureEnabled("component-previews") && componentTemplate.template) {
    return (
      <IsolatedReploComponent
        component={componentTemplate.template}
        variant="desktop"
      />
    );
  }

  if (imageError) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center p-4">
        <ImageOff size={24} className="text-muted mb-2" />
        <p className="text-muted text-center text-2xs">No preview available</p>
      </div>
    );
  }

  const optimizedImageUrl = getOptimizedImageUrl(
    componentTemplate.desktopPreviewUrl,
    {
      width: 800,
      fit: "contain",
    },
  );

  return (
    <>
      {imageLoading && (
        <div className="h-full w-full">
          <Skeleton className="h-full w-full rounded-lg" />
        </div>
      )}
      <img
        src={optimizedImageUrl}
        loading="lazy"
        className={twMerge(
          "max-h-full w-full rounded-lg object-contain transition duration-700 ease-in-out",
          imageLoading ? "opacity-0" : "opacity-100",
        )}
        alt={`${componentTemplate.name} preview`}
        onError={() => setImageError(true)}
        onLoad={() => setImageLoading(false)}
      />
    </>
  );
};

export const SectionTemplateListItem = ({
  componentTemplate,
}: {
  componentTemplate: ComponentTemplate;
}) => {
  const [imageError, setImageError] = React.useState(false);

  return (
    <li key={componentTemplate.id}>
      <div
        className={twMerge(
          "border-0.5 border-border bg-sheet shadow-button-selectable relative flex w-full cursor-pointer items-center justify-center rounded-lg",
          imageError && "aspect-[5/2]",
        )}
        data-testid="component-template-draggable"
      >
        <ComponentPreview
          componentTemplate={componentTemplate}
          imageError={imageError}
          setImageError={setImageError}
        />
        <ComponentTemplateDragOverlay
          dragType="section"
          template={componentTemplate}
          identifier={componentTemplate.id}
          scope="left-bar"
          showOutlineOnHover={false}
        />
      </div>
    </li>
  );
};
